import { ButtonRow, Form } from '@ksyos/design-system';
import { useState } from 'react';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ISupervisionEdit } from '../../../supervisions/SupervisionItemEdit';
import type { ICustomControlSupervisionEditProps } from '../../interfaces/';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { SupervisorsAssessmentBiometrics } from '../../../../../../common/enums/supervisorsAssessmentBiometrics';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getSelectedLanguage } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { FormDivider, HeaderDivider, InputCheckbox, InputRadioGroup, InputTextArea } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputTextCheckbox } from '../../../../elements/InputTextCheckbox';
import { OldTfoV1DiagnosticReportAddEditCustomControlFields } from './OldTfoV1DiagnosticReportAddEditCustomControlFields';

export function TfoV1SupervisionEdit(props: Readonly<ICustomControlSupervisionEditProps>) {
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const translate = getSelectedLanguage();

    return (
        <Form submitOnEnter={true}>
            <HeaderDivider text={translate.supervision.qualityProcedure} />
            <HorizontalFormGroup hasErrors={props.errors.has('assessmentBiometrics')}>
                <FormLabel isRequired>Beoordeling foto's</FormLabel>
                <InputRadioGroup
                    error={props.errors.get('assessmentBiometrics')}
                    labels={[
                        translate.supervisorsAssessmentBiometricsEnum.good,
                        translate.supervisorsAssessmentBiometricsEnum.moderate,
                        translate.supervisorsAssessmentBiometricsEnum.poor,
                    ]}
                    name="assessmentBiometrics"
                    onChange={async (value) => await props.handleChange('assessmentBiometrics', value)}
                    onDoValidate={async () => await props.validateOne('assessmentBiometrics')}
                    options={[
                        SupervisorsAssessmentBiometrics.good,
                        SupervisorsAssessmentBiometrics.moderate,
                        SupervisorsAssessmentBiometrics.poor,
                    ]}
                    value={props.supervision.assessmentBiometrics}
                />
            </HorizontalFormGroup>
            <HorizontalFormGroup hasErrors={props.errors.has('toelichting_kwaliteitscontrole')}>
                <FormLabel
                    isRequired={
                        props.supervision.assessmentBiometrics !== null &&
                        props.supervision.assessmentBiometrics !== SupervisorsAssessmentBiometrics.good
                    }
                >
                    Toelichting kwaliteitscontrole
                </FormLabel>
                <InputContainer>
                    <InputCheckbox
                        checked={props.supervision.customControlData.toelichting_kwaliteitscontrole.niet_gedruppeld}
                        error={props.errors.get('customControlData.toelichting_kwaliteitscontrole.niet_gedruppeld')}
                        onChange={async (value) =>
                            await props.handleChangeCustomControl({
                                toelichting_kwaliteitscontrole: {
                                    ...props.supervision.customControlData.toelichting_kwaliteitscontrole,
                                    niet_gedruppeld: value,
                                },
                            })
                        }
                        onDoValidate={async () =>
                            await props.validateOneCustomControl('toelichting_kwaliteitscontrole')
                        }
                        text="Niet gedruppeld"
                    />
                    <InputCheckbox
                        checked={props.supervision.customControlData.toelichting_kwaliteitscontrole.cataract}
                        error={props.errors.get('customControlData.toelichting_kwaliteitscontrole.cataract')}
                        onChange={async (value) =>
                            await props.handleChangeCustomControl({
                                toelichting_kwaliteitscontrole: {
                                    ...props.supervision.customControlData.toelichting_kwaliteitscontrole,
                                    cataract: value,
                                },
                            })
                        }
                        onDoValidate={async () =>
                            await props.validateOneCustomControl('toelichting_kwaliteitscontrole')
                        }
                        text="Cataract"
                    />
                    <InputTextCheckbox
                        error={props.errors.get('customControlData.toelichting_kwaliteitscontrole.anders_namelijk')}
                        onChange={async (value) =>
                            await props.handleChangeCustomControl({
                                toelichting_kwaliteitscontrole: {
                                    ...props.supervision.customControlData.toelichting_kwaliteitscontrole,
                                    anders_namelijk: value,
                                },
                            })
                        }
                        onDoValidate={async () =>
                            await props.validateOneCustomControl('toelichting_kwaliteitscontrole')
                        }
                        placeholder="Anders, namelijk..."
                        text={props.supervision.customControlData.toelichting_kwaliteitscontrole.anders_namelijk}
                    />
                </InputContainer>
            </HorizontalFormGroup>
            <FormDivider />
            <HeaderDivider text={translate.supervision.qualityReport} />
            <OldTfoV1DiagnosticReportAddEditCustomControlFields
                customControlData={props.supervision.customControlData}
                errors={props.errors}
                handleChangeCustomControl={props.handleChangeCustomControl}
                showAdvisedPolicy={false}
                validateOneCustomControl={props.validateOneCustomControl}
            />
            <HorizontalFormGroup>
                <FormLabel>{translate.common.remarks}</FormLabel>
                <InputContainer>
                    <InputTextArea
                        onChange={async (value) => await props.handleChange('remarkGrading', value)}
                        placeholder={translate.common.remarksOrQuestions}
                        value={props.supervision.remarkGrading}
                    />
                </InputContainer>
            </HorizontalFormGroup>
            <ButtonRow justify="right">
                <CancelButton
                    cancel={props.onCancel}
                    entity={ProcessEntity.supervision}
                    serviceCode={props.service.code}
                    serviceType={props.service.serviceType}
                />
                <SaveButton
                    buttonDisabled={isButtonDisabled}
                    save={async () => await saveSupervision()}
                />
                <SaveSendButton
                    buttonDisabled={isButtonDisabled}
                    saveSend={async () => await saveSendSupervision()}
                />
            </ButtonRow>
        </Form>
    );

    async function saveSendSupervision() {
        setIsButtonDisabled(true);

        delete props.supervision.customControlData.toelichting;

        const validatedSupervision = await props.validateSupervision('final');

        if (!validatedSupervision) {
            setIsButtonDisabled(false);

            return;
        }

        const supervisionId = await props.onSaveSupervision('final');

        setIsButtonDisabled(false);

        showServiceToast(
            ToastType.success,
            'toast.submitSuccess',
            props.service,
            'supervision',
            translate.toast.submitDefaultMessage
        );

        await props.onSave(supervisionId);
    }

    async function saveSupervision() {
        setIsButtonDisabled(true);

        delete props.supervision.customControlData.toelichting;

        const validatedSupervision = await props.validateSupervision('draft');

        if (!validatedSupervision) {
            setIsButtonDisabled(false);

            return;
        }

        const supervisionId = await props.onSaveSupervision('draft');

        setIsButtonDisabled(false);

        showServiceToast(
            ToastType.success,
            'toast.draftSuccess',
            props.service,
            'supervision',
            translate.toast.draftDefaultMessage
        );

        await props.onSave(supervisionId);
    }
}

export function tfoV1SupervisionValidateOneCustomControlData(
    name: string,
    supervision: ISupervisionEdit,
    errors: ValidationErrors
) {
    if (name === 'toelichting_kwaliteitscontrole') {
        return tfoV1SupervisionValidateCustomControlData(supervision, errors);
    }

    return errors;
}

export function tfoV1SupervisionValidateCustomControlData(
    supervision: ISupervisionEdit | undefined,
    errors: ValidationErrors
) {
    if (!supervision) {
        return errors;
    }

    if (supervision.assessmentBiometrics === SupervisorsAssessmentBiometrics.good) {
        return errors.remove('toelichting_kwaliteitscontrole');
    }

    const toelichtingWasFilledIn = Object.values(supervision.customControlData.toelichting_kwaliteitscontrole).some(
        (value) => {
            return value !== false && value !== null;
        }
    );

    return toelichtingWasFilledIn
        ? errors.remove('toelichting_kwaliteitscontrole')
        : errors.set('toelichting_kwaliteitscontrole', 'any_required');
}

export function tfoV1SupervisionInitializeCustomControlData() {
    return {
        toelichting_kwaliteitscontrole: {
            anders_namelijk: null,
            cataract: false,
            niet_gedruppeld: false,
        },
    };
}
